/** @format */

import {forwardRef, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import NumberFormat from 'react-number-format';
import {Icon} from '@sttark/design-system';

export const Input = forwardRef(
    (
        {type, rows, label, value, onChange, name, disabled, error, className, suffix, ...props},
        ref
    ) => {
        // delete newestds prop that we use in v2frontier to choose between old design systems and new ones
        delete props.newestds;

        const hasError = !!error;
        const isPassword = type === 'password';

        var InputTag;
        if (rows > 0) {
            InputTag = 'textarea';
        } else if (type === 'number') {
            InputTag = NumberFormat;
        } else {
            InputTag = 'input';
        }

        const fieldClass = classNames('field', hasError && 'field--error');
        const inputClass = classNames('control', className, {'w-100': !props.size});

        const handleChange = useCallback(e => onChange?.(e.target.value), [onChange]);

        const shouldRenderInputLabel =
            !isPassword || !(isPassword && value !== ' ' && value === '');

        return (
            <div className={fieldClass}>
                {!isPassword && (
                    <InputTag
                        ref={ref}
                        className={inputClass}
                        placeholder={label}
                        suffix={suffix}
                        name={name}
                        id={name}
                        disabled={disabled}
                        value={value}
                        rows={rows}
                        aria-invalid={hasError}
                        onChange={handleChange}
                        {...props}
                    />
                )}

                {isPassword && (
                    <PasswordInput
                        ref={ref}
                        className={className}
                        placeholder={label}
                        suffix={suffix}
                        name={name}
                        id={name}
                        disabled={disabled}
                        value={value}
                        rows={rows}
                        hasError={hasError}
                        onChange={handleChange}
                        {...props}
                    />
                )}

                {shouldRenderInputLabel && (
                    <label className="floating-label" htmlFor={name}>
                        <span className={isPassword ? 'color-alert-positive' : ''}>{label}</span>
                    </label>
                )}

                {hasError && <p className="mt-1 color-alert-negative">{error}</p>}
            </div>
        );
    }
);

// does not share may features and effects as the rest of the
// inputs, therefore is treated separately
function PasswordInput({
    ref,
    type,
    rows,
    label,
    value,
    name,
    disabled,
    error,
    className,
    suffix,
    hasError,
    onChange,
    ...props
}) {
    const [isHidden, setIsHidden] = useState(true);

    const inputClass = classNames('control position-relative', className, {'w-100': !props.size});
    return (
        <div className="position-relative password-input">
            <input
                ref={ref}
                className={inputClass}
                type={isHidden ? 'password' : 'text'}
                placeholder={label}
                suffix={suffix}
                name={name}
                id={name}
                disabled={disabled}
                value={value}
                rows={rows}
                aria-invalid={hasError}
                onChange={onChange}
                {...props}
            />
            <div
                className="position-absolute password-input_status d-flex align-items-center justify-content-end"
                onClick={() => setIsHidden(!isHidden)}>
                <span className="color-structure-dark d-block pb-1 pe-2">
                    {isHidden ? 'Reveal' : 'Hide'}
                </span>

                <Icon name={isHidden ? 'view' : 'hide'} small color="#a3abb2" />
            </div>
        </div>
    );
}

Input.displayName = 'Input';

Input.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string,
    type: PropTypes.string,
    suffix: PropTypes.string,
    className: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    value: PropTypes.any,
    error: PropTypes.string,
    rows: PropTypes.number,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Input.defaultProps = {
    label: 'Label Name'
};
